import { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { jwtDecode } from 'jwt-decode';
import { GlobalContext } from 'GlobalContext';

const toExpiryTimestamp = (expiresInSeconds) => {
    const seconds = Number(expiresInSeconds);
    if (!Number.isFinite(seconds) || seconds <= 0) {
        return null;
    }

    return String(Date.now() + Math.floor(seconds * 1000));
};

const STORAGE_KEYS = {
    state: 'oauth_state',
    codeVerifier: 'oauth_code_verifier',
    scope: 'oauth_scope',
    returnTo: 'oauth_return_to',
};

const DEFAULT_SCOPE = 'timekeeper scheduled employee auth mdm_psc custom_reports';

const clearOAuthTempStorage = () => {
    sessionStorage.removeItem(STORAGE_KEYS.state);
    sessionStorage.removeItem(STORAGE_KEYS.codeVerifier);
    sessionStorage.removeItem(STORAGE_KEYS.scope);
    sessionStorage.removeItem(STORAGE_KEYS.returnTo);
};

const getPermissionsIdListFromHex = (permissionsHex) => {
    const numero = BigInt(`0x${permissionsHex || '0'}`);
    const binary = numero.toString(2);

    return [...binary]
        .map((bit, i) => (bit === '1' ? i + 1 : null))
        .filter((id) => id !== null);
};

const getPermissionsListFromId = (permissionsIds, permissionsNameList) =>
    permissionsIds
        .map((id) => permissionsNameList.find((permission) => permission.permission_id === id)?.name)
        .filter((name) => name !== undefined);

const exchangeCodeForToken = async ({ code, codeVerifier, scope, clientId }) => {
    const endpoint = `${process.env.REACT_APP_TK_GW_DOMAIN}/v1/auth/v1/token`;

    const body = new FormData();
    body.append('grant_type', 'authorization_code');
    body.append('client_id', clientId);
    body.append('scope', scope);
    body.append('code', code);
    body.append('code_verifier', codeVerifier);

    const response = await fetch(endpoint, {
        method: 'POST',
        body,
    });

    if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        const detail = errorBody?.detail || 'Error intercambiando code por token';
        throw new Error(detail);
    }

    return response.json();
};

export const OAuthCallback = () => {
    const navigate = useNavigate();
    const { setAuthenticated } = useContext(GlobalContext);
    const [error, setError] = useState('');
    const didProcess = useRef(false);

    useEffect(() => {
        if (didProcess.current) {
            return;
        }
        didProcess.current = true;

        const processCallback = async () => {
            const params = new URLSearchParams(window.location.search);
            const code = params.get('code');
            const returnedState = params.get('state');
            const oauthError = params.get('error');
            const oauthErrorDescription = params.get('error_description');

            const storedState = sessionStorage.getItem(STORAGE_KEYS.state);
            const codeVerifier = sessionStorage.getItem(STORAGE_KEYS.codeVerifier);
            const scope = sessionStorage.getItem(STORAGE_KEYS.scope) || DEFAULT_SCOPE;
            const returnTo = sessionStorage.getItem(STORAGE_KEYS.returnTo) || '/';
            const clientId = `${process.env.REACT_APP_CLIENT_ID || ''}`.replace(/"/g, '').trim();

            try {
                if (oauthError) {
                    throw new Error(oauthErrorDescription || oauthError);
                }

                if (!code) {
                    throw new Error('No se recibio code en el callback');
                }

                if (!storedState || !returnedState || storedState !== returnedState) {
                    throw new Error('State invalido en callback');
                }

                if (!codeVerifier) {
                    throw new Error('No se encontro code_verifier para finalizar login');
                }

                if (!clientId) {
                    throw new Error('Falta REACT_APP_CLIENT_ID');
                }

                const tokenResponse = await exchangeCodeForToken({
                    code,
                    codeVerifier,
                    scope,
                    clientId,
                });

                const decodedToken = jwtDecode(tokenResponse.access_token);
                const permissionsIds = getPermissionsIdListFromHex(decodedToken?.permissions_hex);
                const allPermissions = JSON.parse(localStorage.getItem('all_permissions') || '[]');
                const permissions = getPermissionsListFromId(permissionsIds, allPermissions);

                localStorage.setItem('access_token', tokenResponse.access_token);
                localStorage.setItem('token_type', tokenResponse.token_type);
                localStorage.setItem('expires_in', tokenResponse.expires_in);
                const expiresAt = toExpiryTimestamp(tokenResponse.expires_in);
                if (expiresAt) {
                    localStorage.setItem('expires_at', expiresAt);
                }
                localStorage.setItem('username', decodedToken?.username || '');
                if (tokenResponse.refresh_token) {
                    localStorage.setItem('refresh_token', tokenResponse.refresh_token);
                }
                if (tokenResponse.refresh_expires_in) {
                    localStorage.setItem('refresh_expires_in', tokenResponse.refresh_expires_in);
                    const refreshExpiresAt = toExpiryTimestamp(tokenResponse.refresh_expires_in);
                    if (refreshExpiresAt) {
                        localStorage.setItem('refresh_expires_at', refreshExpiresAt);
                    }
                }
                localStorage.setItem('permissions', JSON.stringify(permissions));
                sessionStorage.setItem('authenticated', 'true');
                setAuthenticated(true);

                clearOAuthTempStorage();
                navigate(returnTo, { replace: true });
            } catch (err) {
                clearOAuthTempStorage();
                localStorage.removeItem('access_token');
                localStorage.removeItem('token_type');
                localStorage.removeItem('expires_in');
                localStorage.removeItem('expires_at');
                localStorage.removeItem('refresh_token');
                localStorage.removeItem('refresh_expires_in');
                localStorage.removeItem('refresh_expires_at');
                localStorage.removeItem('permissions');
                sessionStorage.removeItem('authenticated');
                setAuthenticated(false);
                setError(err instanceof Error ? err.message : 'No se pudo completar el login OAuth');

                window.history.replaceState({}, document.title, window.location.pathname);
            }
        };

        processCallback();
    }, [navigate, setAuthenticated]);

    if (!error) {
        return (
            <Box
                sx={{
                    minHeight: '100vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 3,
                }}
            >
                <Stack spacing={2} alignItems='center' sx={{ maxWidth: 560, textAlign: 'center' }}>
                    <CircularProgress size={28} />
                    <Typography variant='h6'>Iniciando sesión</Typography>
                    <Typography variant='body2' color='text.secondary'>
                        Estamos validando tu autenticación y preparando tu sesión...
                    </Typography>
                </Stack>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 3,
            }}
        >
            <Stack spacing={2} alignItems='center' sx={{ maxWidth: 560, textAlign: 'center' }}>
                <Typography variant='h6'>No se pudo completar el login</Typography>
                <Typography variant='body2' color='text.secondary'>
                    {error}
                </Typography>
                <Button variant='contained' onClick={() => navigate('/signin', { replace: true })}>
                    Volver a iniciar sesion
                </Button>
            </Stack>
        </Box>
    );
};
